import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { Keyboard, View } from 'react-native';
import { emojify } from 'node-emoji';
import { AppView } from 'design-system/AppView';
import EmojiText from 'design-system/EmojiText';
import { CardView } from 'design-system/CardView';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem } from 'design-system/values';
import { IconEmma, IconText, IconTrash } from 'design-system/icons';
import EmojiPickerModal from 'design-system/emojiPicker/EmojiPickerModal';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { SettingsItemInput, SettingsItemNavigate, } from 'design-system/SettingsItem';
import Squircle from 'design-system/Squircle';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useColors from 'hooks/useColors';
import Alert from 'utils/packages/Alert';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { webPickerWidth } from 'features/emoji-picker/components/EmojiSelector';
import { Amplitude } from 'utils';
import { getCategories, updateCategory } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import checkFeatureFlag from 'utils/featureFlags';
import { selectCategory, selectCategoryHasSubcategories, } from 'reducers/selectors';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import Modal from 'utils/packages/Modal';
import { IconCategory } from 'design-system/icons/IconCategory';
import { isWeb } from '../../../constants';
import useDeleteCategory from '../hooks/useDeleteCategory';
import WebEmojiPicker from '../components/WebEmojiPicker.web';
import CustomCategoryTransactionsPreview from '../components/categories/CustomCategoryTransactionsPreview';
import IconTransactionCategory from '../icons/IconTransactionCategory';
import SubcategoriesList from '../components/categories/SubcategoriesList';
import { readColorKeyOfRawCategory, writeColorKeyToRawCategory, } from '../utils';
const CustomCategoryEditScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const showSubcategoriesCreateFlow = useAppSelector((store) => checkFeatureFlag(store.utils.featureFlags, 'show_subcategories_create_flow'));
    const colors = useColors();
    const nameInputRef = useRef(null);
    const emojiViewRef = useRef(null);
    const { paddingHorizontalStyle, sidebarWidth, width } = useAppFrameDimensions(isWeb);
    const [webEmojiPickerState, setWebEmojiPickerState] = useState(undefined);
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [category, setCategory] = useState(route.params.category);
    useEffect(() => {
        setCategory(route.params.category);
    }, [route.params.category]);
    const parentCategory = useAppSelector((store) => selectCategory(store, category.parentCategoryId)) ?? null;
    const { displayName, emoji, parentCategoryId } = category;
    const colorKey = readColorKeyOfRawCategory(category);
    const [isFetchingDelete, deleteCategory] = useDeleteCategory();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<NavigationHeaderRightIconButton onPress={() => {
                    deleteCategory(category.id, () => {
                        navigation.pop();
                    });
                }}>
              <IconTrash colorKey="primary"/>
            </NavigationHeaderRightIconButton>),
        });
    }, [category, deleteCategory]);
    const clearWebEmojiPickerState = useCallback(() => {
        setIsEmojiPickerOpen(false);
        setWebEmojiPickerState(undefined);
    }, []);
    const onEmojiModalClose = useCallback(() => {
        setIsEmojiPickerOpen(false);
    }, []);
    const onSetEmoji = useCallback((emoji) => {
        setCategory((old) => ({ ...old, emoji }));
        setWebEmojiPickerState(undefined);
        onEmojiModalClose();
    }, [onEmojiModalClose]);
    const dispatch = useAppDispatch();
    const onPressEmoji = useCallback(() => {
        Keyboard.dismiss();
        setIsEmojiPickerOpen(true);
        if (isWeb) {
            emojiViewRef.current?.measure((_, __, ___, ____, fx, fy) => {
                setWebEmojiPickerState({
                    fx: fx - sidebarWidth + (width - webPickerWidth - rem(64)),
                    fy,
                });
            });
        }
        else {
            EmojiPickerModal.show(emoji || undefined, onSetEmoji, onEmojiModalClose);
        }
    }, [emoji, onEmojiModalClose, onSetEmoji, sidebarWidth, width]);
    const hasSubcategories = useAppSelector((store) => selectCategoryHasSubcategories(store, category.id));
    useEffect(() => {
        if (hasSubcategories) {
            setCategory((old) => ({ ...old, parentCategoryId: null }));
        }
    }, [hasSubcategories]);
    const onPressParentCategory = useCallback(() => {
        Keyboard.dismiss();
        if (hasSubcategories) {
            Modal.showConfirmation({
                title: 'Cannot convert category',
                subtitle: 'This category has its own subcategories. To convert it into a subcategory, you must first remove or reassign all of its existing subcategories.',
                confirmButtonTitle: 'Okay',
            });
            return;
        }
        navigation.navigate('PickCategory', {
            hideExcluded: true,
            hideSubcategories: true,
            hiddenCategories: [category.id],
            title: 'Pick a parent category',
            subtitle: 'Choose “None” if you want this to be a main category',
            category: parentCategory || {
                id: 'none',
                displayName: 'None',
                emoji: null,
            },
            onPickCategory: (data) => {
                setCategory((old) => ({
                    ...old,
                    parentCategoryId: data?.id ?? null,
                }));
                navigation.pop();
            },
        });
    }, [category.id, hasSubcategories, parentCategory]);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    // color is edited in another screen and updated in route params, therefore not compared here.
    const hasEdited = route.params.hasEdited ||
        route.params.category.displayName !== displayName ||
        route.params.category.emoji !== emoji ||
        route.params.category.parentCategoryId !== parentCategoryId;
    const [, , isSaving, fetch] = useFetchOld(muteErrorAndResult);
    const onPressSave = useCallback(async () => {
        clearWebEmojiPickerState();
        if (!displayName.trim().length) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must enter a name.');
            return;
        }
        if (!emoji) {
            Alert.alert(emojify('Hold on :dizzy:'), 'You must choose an emoji.');
            return;
        }
        if (hasSubcategories && parentCategoryId) {
            Modal.showConfirmation({
                title: 'Cannot convert category',
                subtitle: 'This category has its own subcategories. To convert it into a subcategory, you must first remove or reassign all of its existing subcategories.',
                confirmButtonTitle: 'Okay',
            });
            return;
        }
        const response = await fetch(updateCategory(category.id, writeColorKeyToRawCategory({
            displayName: displayName.trim(),
            emoji: emoji || null,
            parentCategoryId,
        }, colorKey)));
        if (!response)
            return;
        dispatch(getCategories());
        Amplitude.logEvent('Premium.CustomCategoryUpdated');
        navigation.pop();
    }, [
        category.id,
        clearWebEmojiPickerState,
        colorKey,
        displayName,
        emoji,
        hasSubcategories,
        parentCategoryId,
    ]);
    return (<AppView type="secondary" withFrame={false}>
          <AnimatedKeyboardAwareScrollView scrollEventThrottle={16} showsVerticalScrollIndicator={false} onScroll={scrollHandler} enableOnAndroid contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="handled">
            <View style={paddingHorizontalStyle}>
              <CardView webPadding noMarginBottom>
                <SettingsItemInput description="Name" icon={<IconText gradientKey="darkPink"/>} value={displayName} onChangeText={(displayName) => setCategory((old) => ({ ...old, displayName }))} placeholder="Name your category..." iconSize={44} ref={nameInputRef} returnKeyType="done" {...(isWeb && {
        style: cursorStyle,
        onPress: clearWebEmojiPickerState,
    })}/>
                <View ref={emojiViewRef}>
                  <SettingsItemNavigate title={emoji ? 'Change' : 'Choose...'} description="Emoji" icon={emoji ? (<EmojiText fontSize={20}>{emoji}</EmojiText>) : (<IconEmma />)} iconSize={44} onPress={onPressEmoji} isSelected={isEmojiPickerOpen}/>
                </View>
                {parentCategory === null && (<SettingsItemNavigate title={'Change'} description={'Colour'} icon={<Squircle color={colors.categories[colorKey ?? 'custom']} width={24} height={24}/>} iconSize={44} onPress={() => {
                navigation.navigateDeprecated('CustomCategoryChooseColorSheet', {
                    category,
                });
            }} isSelected={false}/>)}
                {!!showSubcategoriesCreateFlow && (<SettingsItemNavigate description="Parent category (optional)" title={parentCategory?.displayName || 'None'} icon={parentCategory ? (<IconTransactionCategory width={rem(24)} height={rem(24)} category={parentCategory}/>) : (<IconCategory alt color={colors.categories.custom}/>)} onPress={onPressParentCategory} isSelected={false}/>)}
              </CardView>
              {!!showSubcategoriesCreateFlow &&
            !route.params.category.parentCategoryId &&
            !category.parentCategoryId && (<SubcategoriesList parentCategory={category}/>)}
              <CustomCategoryTransactionsPreview category={category}/>
            </View>
          </AnimatedKeyboardAwareScrollView>
          {!!hasEdited && (<Button brand floating title="Save" onPress={onPressSave} disabled={isFetchingDelete} isFetching={isSaving}/>)}
          {isWeb ? (<WebEmojiPicker {...webEmojiPickerState} onEmojiSelected={onSetEmoji} defaultSelectedEmoji={emoji || undefined} clearWebEmojiPickerState={clearWebEmojiPickerState}/>) : null}
        </AppView>);
}, ({ colors }) => ({
    title: 'Edit custom category',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const cursorStyle = {
    cursor: 'text',
    margin: 0,
    paddingVertical: rem(16),
    paddingHorizontal: rem(8),
};
export default CustomCategoryEditScreen;
